import React, { useState, useEffect } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../supabaseClient';

const ScatterPlotPage = () => {
  const [data, setData] = useState([]);
  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState('');
  const [categories, setCategories] = useState({
    General: [
      'Search Bar', 'ProfileView(header)', 'Notification Logo (Bell)', 'CIBIL/Credit Score Check',
      'AI Assistant', 'Voice/Speech Assist', 'Learning Resources', 'Customer Support/Help'
    ],
    Payments: [
      'Pay Contacts', 'QR Scanner', 'Bank Transfer', 'Bank Balance', 'UPI Lite (Balance/Start)',
      'Self UPI ID', 'Tap & Pay', 'Transaction History', 'Self Transfer', 'Pay UPI ID'
    ],
    Promotional: [
      'Advertisement Banner', 'Referrals Program', 'Check Rewards Points', 'Offers',
      'Sponsored Links', 'Rewards', 'Games'
    ],
    Bill: [
      'Mobile Recharge', 'DTH/Cable TV', 'FASTag Recharge', 'LPG booking', 'Pay Rent',
      'EMI Payments', 'Broadband', 'Credit Card Payment', 'Education fees', 'Electricity', 'Pay Bills'
    ],
    Investments: [
      'Wealth', 'Invest In Gold Funds', 'Invest in Digital Gold', 'Lend Money',
      'Fixed Deposits', 'Invest in Mutual Funds', 'Check EPF account'
    ],
    Loans: [
      'Zero Interest Payment Loans', 'Instant Pre Approved Loans', 'Gold Loan', 'Mutual Fund Loan',
      'Bike Loan', 'Commercial Vehicle Loan', 'Tractor & Farm Loan', 'Home Loan',
      'Loan Against Property', 'Personal Loans', 'Apply for Credit Card'
    ],
    'E-Com/Travel': [
      'Access Item Shop', 'Categories Of Shopping products', 'Bus', 'Trains', 'Metro',
      'Electronics', 'Hotels', 'Flights', 'Prepaid Cabs', 'Roadside Assistance'
    ],
    Insurance: [
      'Two Wheeler Insurance', 'Four Wheeler Insurance', 'Health Insurance',
      'Life Insurance', 'Home Insurance', 'Locator'
    ]
  });

  const companies = [
    'navi', 'bajaj', 'shriram_one', 'aditya_birla', 'kiwi', 'bharatpe', 'pop_upi', 
    'genwise', 'samsung_pay', 'onecard', 'ultracash', 'groww', 'timepay', 
    'moneytap', 'google_pay', 'phonepe', 'freecharge', 'paytm', 'slice', 'niyo', 'jio_finance','sbi_yono','axis_bank'
  ];

  const colors = [
    '#FF6633', '#FFB399', '#FF33FF', '#FFFF99', '#00B3E6', '#E6B333', '#3366E6', '#999966', '#99FF99', '#B34D4D',
    '#80B300', '#809900', '#E6B3B3', '#6680B3', '#66991A', '#FF99E6', '#CCFF1A', '#FF1A66', '#E6331A', '#33FFCC', '#E6332A'
  ];

  const [nullApps, setNullApps] = useState([]);

  useEffect(() => {
    fetchFeatures();
  }, []);

  useEffect(() => {
    if (selectedFeature) {
      fetchData(selectedFeature);
    }
  }, [selectedFeature]);

  const fetchFeatures = async () => {
    const { data, error } = await supabase
      .from('app_features')
      .select('feature')
      .order('feature');

    if (error) {
      console.error('Error fetching features:', error);
    } else {
      setFeatures(data.map(item => item.feature));
      setSelectedFeature('Search Bar');
    }
  };

  const fetchData = async (feature) => {
    const { data: featureData, error } = await supabase
      .from('app_features')
      .select('*')
      .eq('feature', feature)
      .single();

    if (error) {
      console.error('Error fetching data:', error);
    } else {
      const coordinateGroups = {};
      const nullAppsList = [];
      companies.forEach((company, index) => {
        const coordinates = featureData[company] ? featureData[company].split(',').map(Number) : [null, null];
        if (coordinates[0] === null || coordinates[1] === null || coordinates[0] === 0 || coordinates[1] === 0) {
          nullAppsList.push(company);
        } else {
          const key = `${coordinates[1]},${coordinates[0]}`;
          if (!coordinateGroups[key]) {
            coordinateGroups[key] = {
              x: coordinates[1],
              y: coordinates[0],
              color: colors[Object.keys(coordinateGroups).length % colors.length],
              apps: []
            };
          }
          coordinateGroups[key].apps.push(company);
        }
      });

      const formattedData = Object.values(coordinateGroups);
      setData(formattedData);
      setNullApps(nullAppsList);
    }
  };

  const CustomizedDot = (props) => {
    const { cx, cy, payload } = props;
    if (payload.x === null || payload.y === null || payload.x === 0 || payload.y === 0) return null;
    
    // Calculate the radius based on the number of apps
    const baseRadius = 4;
    const radius = baseRadius + (payload.apps.length - 1) * 3;
    return (
      <circle cx={cx} cy={cy} r={radius} fill={payload.color} />
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length && payload[0].payload) {
      const data = payload[0].payload;
      if (data.x === null || data.y === null || data.x === 0 || data.y === 0) return null;
      return (
        <div className="custom-tooltip bg-white p-2 border border-gray-300 rounded shadow">
          <p className="font-bold"> {data.apps.length} Apps at this point</p>
          {data.apps.map((app, index) => (
            <p key={index}>
              {app.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Filter data for the scatter plot and legend
  const filteredData = data.filter(d => d.x > 0 && d.y > 0);

  return (
    <div className="ScatterPlotPage p-4">
      <h1 className="text-2xl font-bold mb-4 italic relative inline-block">
        App Feature Plot (Home Screen)
        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-amber-300"></span>
      </h1>
      <div className="mb-4">
        <label htmlFor="feature-select" className="mr-2">Select a feature:</label>
        <select
          id="feature-select"
          value={selectedFeature}
          onChange={(e) => setSelectedFeature(e.target.value)}
          className="border rounded p-1"
        >
          {Object.entries(categories).map(([category, features]) => (
            <optgroup key={category} label={category}>
              {features.map((feature) => (
                <option key={feature} value={feature}>{feature}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-1/3 mb-4 md:mb-0">
          <div className="phone-frame bg-gray-800 rounded-3xl p-3 shadow-lg max-w-xs mx-auto">
            <div className="phone-screen bg-white rounded-2xl overflow-hidden" style={{ aspectRatio: '9/16' }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{
                    top: 10,
                    right: 10,
                    bottom: 10,
                    left: 10,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                  <XAxis type="number" dataKey="x" domain={[0, 50]} hide={true} />
                  <YAxis type="number" dataKey="y" domain={[0, 100]} hide={true} />
                  <Tooltip content={<CustomTooltip />} />
                  <Scatter
                    name={selectedFeature}
                    data={filteredData}
                    shape={<CustomizedDot />}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="w-full md:w-2/3 md:pl-4">
          <h2 className="text-xl font-bold mb-2">Apps Feature At This Point</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {filteredData
            .sort((a, b) => b.apps.length - a.apps.length)
            .map((entry, index) => (
              <div key={index} className="flex items-start">
                <div 
                  className="mr-2 mt-1 flex-shrink-0 square-full" 
                  style={{ 
                    backgroundColor: entry.color,
                    width: `${14}px`,
                    height: `${11}px`
                  }}
                ></div>
                <div className="text-sm">
                  {entry.apps.map((app, appIndex) => (
                    <div key={appIndex}>
                      {app.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {nullApps.length > 0 && (
            <div className="mt-8 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-bold mb-2">❌ Apps not showing: <span className="underline bg-amber-100 text-black-500">{selectedFeature}</span></h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {nullApps.map((app, index) => (
                  <div key={index} className="text-sm">
                    {app.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScatterPlotPage;
